<template>
  <PageSeoData>
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone-mob@2x.webp"
      type="image/webp"
    />
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone-mob.webp"
      type="image/webp"
    />
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone-tab.webp"
      type="image/webp"
    />
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone-tab@2x.webp"
      type="image/webp"
    />
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone.webp"
      type="image/webp"
    />
    <Link
      rel="preload"
      fetchpriority="high"
      as="image"
      href="/image/suggestionSection/en-lt/phone@2x.webp"
      type="image/webp"
    />
    <template #scripts>
      <Link
        rel="stylesheet"
        :href="'https://api.mapbox.com/mapbox-gl-js/v3.24.0/mapbox-gl.css'"
        fetchpriority="low"
        defer
        media="screen and (min-width: 1280px)"
      />
    </template>
  </PageSeoData>
  <SuggestionSection :page="User.members" :is-statistics="true">
    <template #images>
      <PictureComponent
        :modern-format-image="{
          'mobile-path': `/image/suggestionSection/en-cy/phone-mob.webp`,
          'mobile-path-retina': `/image/suggestionSection/en-cy/phone-mob@2x.webp`,
          'tablet-path': `/image/suggestionSection/en-cy/phone-tab.webp`,
          'tablet-path-retina': `/image/suggestionSection/en-cy/phone-tab@2x.webp`,
          'desktop-path': `/image/suggestionSection/en-cy/phone.webp`,
          'desktop-path-retina': `/image/suggestionSection/en-cy/phone@2x.webp`,
        }"
        :fallback-format-image="{
          'mobile-path': `/image/suggestionSection/en-cy/phone-mob.png`,
          'mobile-path-retina': `/image/suggestionSection/en-cy/phone-mob@2x.png`,
          'tablet-path': `/image/suggestionSection/en-cy/phone-tab.png`,
          'tablet-path-retina': `/image/suggestionSection/en-cy/phone-tab@2x.png`,
          'desktop-path': `/image/suggestionSection/en-cy/phone.png`,
          'desktop-path-retina': `/image/suggestionSection/en-cy/phone@2x.png`,
        }"
        loading="auto"
        fetchpriority="high"
      />
    </template>
  </SuggestionSection>
  <div class="section-wrapper" id="advantagesSection">
    <LazySectionsAdvantagesSection v-if="sectionsState.advantagesSection" brand="SportBenefit" />
  </div>
  <div
    ref="levelsSection"
    class="section-wrapper"
    id="levelsSection"
  >
    <LazySectionsLevelSection
      :levels-info="levelsInfo"
      v-if="sectionsState.levelsSection && levelsInfo"
    />
  </div>
  <div class="section-wrapper" id="partnersLogoSection">
    <LazySectionsPartnersLogoSection
      :page="User.companies"
      :partner-companies="companyContactData.companies"
      v-if="sectionsState.partnersLogoSection"
    />
  </div>
  <div class="section-wrapper" id="faqSection">
    <LazySectionsFAQSection v-if="sectionsState.faqSection" />
  </div>
  <div class="section-wrapper" id="getDetailsSection">
    <LazySectionsGetDetailsSection v-if="sectionsState.getDetailsSection" />
  </div>
  <div class="section-wrapper" id="contactsSection">
    <LazySectionsContactsSection
      :office-contact="companyContactData.officeContact"
      v-if="sectionsState.contactsSection"
    />
  </div>
</template>

<script setup lang="ts">
import getOrganizationScheme from "~/schemas/OrganizationScheme";
import getFAQScheme from "~/schemas/FAQScheme";
import { User } from "~/types/entities";
import SuggestionSection from "~/components/sections/SuggestionSection.vue";
import PageSeoData from "~/components/pageSeoData/PageSeoData.vue";
import { type Content } from "~/stores/content";
import { useCountriesStore } from "~/stores/countries";
import { loadLevelsInfo, levelsInfoFetchKey } from "~/components/sections/level-section";
import { getContentOrDefault} from "~/utils/contentPrepare";

const route = useRoute();
const { currentCountry } = storeToRefs(useCountriesStore());
const levelsInfoKey = computed(
  () => `${levelsInfoFetchKey}:${route.path}:${currentCountry.value.code}:active`
);
const { data: levelsInfo } = await useAsyncData(levelsInfoKey, () => loadLevelsInfo({ countryCode: currentCountry.value.code }));
const props = defineProps<{ content?: Content | null }>();
const companyContactData = computed(() => getContentOrDefault(props.content));

const sectionsState = reactive({
  advantagesSection: false,
  levelsSection: false,
  partnersLogoSection: false,
  faqSection: false,
  videoSection: false,
  getDetailsSection: false,
  contactsSection: false,
});

useSchemaOrg([getOrganizationScheme(), getFAQScheme(User.members)]);

const observer = process.client ? new IntersectionObserver(onIntersect) : null;

function onIntersect(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      sectionsState[entry.target.id] = true;
      observer?.unobserve(entry.target);
    }
  });
}

onMounted(() => {
  const sections = document.querySelectorAll(".section-wrapper");
  sections.forEach((section) => observer?.observe(section));
});
</script>
